import { useEffect, useRef, type CSSProperties } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { formatClock, nudgeStyle, type NudgeIconKey } from './api';
import type { LiveConnection, ScreenContext, StreamNudge } from './hooks';
import './livecoach.scss';

/* inline icons (these map to @kula-ai/icons in production) */
const Eye = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const Check = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    {...p}
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const Sparkle = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
    <circle cx="18.5" cy="5" r="1.1" fill="currentColor" />
  </svg>
);

// Per-category glyph for the rail icon. Stroke-only so it tints with the card's accent.
const NUDGE_ICONS: Record<NudgeIconKey, JSX.Element> = {
  time: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" />
    </>
  ),
  coverage: (
    <>
      <path
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  probe: (
    <>
      <path
        d="M12 2a7 7 0 00-4 12.7V18h8v-3.3A7 7 0 0012 2z"
        strokeLinejoin="round"
      />
      <path d="M9 21h6" strokeLinecap="round" />
    </>
  ),
  drift: (
    <>
      <path
        d="M3 12h7l2-5 3 10 2-5h4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  talk: (
    <>
      <path d="M8 10h8M8 14h5" strokeLinecap="round" />
      <path
        d="M21 12a8 8 0 01-8 8H7l-4 3 1.2-4.6A8 8 0 1121 12z"
        strokeLinejoin="round"
      />
    </>
  ),
  wind: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l-3 2" strokeLinecap="round" />
    </>
  ),
};

const NudgeIcon = ({ icon }: { icon: NudgeIconKey }) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    {NUDGE_ICONS[icon]}
  </svg>
);

// The connection pill, top-right of the header. Shows the WORSE of the panel↔server link and the meeting
// feed: Live (green) / Reconnecting… (amber, pulsing) / Disconnected (red, terminal). Single line, never
// wraps — the label carries the detail, and a title tooltip carries the "what to do".
const CONNECTION_PILL: Record<
  LiveConnection['state'],
  { label: string; dot: string; bg: string; border: string; fg: string }
> = {
  live: {
    label: 'Live',
    dot: 'status-4',
    bg: 'rgba(79,208,138,0.12)',
    border: 'rgba(79,208,138,0.3)',
    fg: 'pastel-2-dark',
  },
  reconnecting: {
    label: 'Reconnecting…',
    dot: 'rgba(214,158,46,1)',
    bg: 'rgba(214,158,46,0.12)',
    border: 'rgba(214,158,46,0.32)',
    fg: 'rgba(146,101,0,1)',
  },
  disconnected: {
    label: 'Disconnected',
    dot: 'rgba(224,80,80,1)',
    bg: 'rgba(224,80,80,0.1)',
    border: 'rgba(224,80,80,0.3)',
    fg: 'rgba(168,42,42,1)',
  },
};

const connectionTooltip = (c: LiveConnection): string => {
  if (c.state === 'live') return 'Connected — coaching live.';
  if (c.state === 'reconnecting') {
    return c.scope === 'feed'
      ? 'Re-establishing the meeting feed — nudges will resume when it reconnects.'
      : 'Reconnecting to the coaching service…';
  }
  return c.scope === 'feed'
    ? 'The meeting feed stopped and cannot be recovered here — a page refresh will not help. Nudges have ended for this call.'
    : 'Lost connection to the coaching service. Refresh the page to retry.';
};

const ConnectionBadge = ({ connection }: { connection: LiveConnection }) => {
  const s = CONNECTION_PILL[connection.state];
  return (
    <Flex
      flex="none"
      align="center"
      gap="7px"
      h="30px"
      px="13px"
      borderRadius="20px"
      bg={s.bg}
      border="1px solid"
      borderColor={s.border}
      title={connectionTooltip(connection)}
      whiteSpace="nowrap"
    >
      <Box
        className={
          connection.state === 'reconnecting' ? 'livecoach-live-dot' : undefined
        }
        w="7px"
        h="7px"
        borderRadius="50%"
        bg={s.dot}
      />
      <KText
        size="xs-semi-bold"
        color={s.fg}
        fontSize="11.5px"
        fontWeight={600}
      >
        {s.label}
      </KText>
    </Flex>
  );
};

// A small purple "DEMO" pill shown next to the Live badge when the session was started by the demo harness,
// so a simulated round can never be mistaken for a real candidate call.
const DemoBadge = () => (
  <Flex
    flex="none"
    align="center"
    h="30px"
    px="11px"
    borderRadius="20px"
    bg="rgba(96,90,251,0.1)"
    border="1px solid"
    borderColor="rgba(96,90,251,0.28)"
  >
    <KText
      size="xs-semi-bold"
      color="primary-600"
      fontSize="10.5px"
      fontWeight={700}
      letterSpacing="0.6px"
    >
      DEMO
    </KText>
  </Flex>
);

// One compact nudge: the ready-to-say question first, then its evidence in italics. Category controls
// the original card, label, and icon colours. Resolution dims and strikes the question.
function NudgeCard({
  nudge,
  isLast,
  onDiscuss,
}: {
  nudge: StreamNudge;
  isLast: boolean;
  onDiscuss?: (nudge: StreamNudge) => void;
}) {
  const style = nudgeStyle(nudge.category);
  const light = `${style.palette}-light`;
  const dark = `${style.palette}-dark`;
  const attention = style.attention;
  return (
    <Flex className="livecoach-nudge" gap="14px" pt="18px">
      <Flex direction="column" align="center" flex="none" w="34px">
        <Flex
          align="center"
          justify="center"
          w="34px"
          h="34px"
          borderRadius="10px"
          bg={light}
          color={dark}
          boxShadow="0 6px 16px -8px rgba(38,34,68,0.4)"
          zIndex={1}
        >
          <NudgeIcon icon={style.icon} />
        </Flex>
        {!isLast && (
          <Box
            flex={1}
            w="2px"
            mt="6px"
            borderRadius="2px"
            bgGradient="linear(var(--kula-colors-secondary-200), transparent)"
          />
        )}
      </Flex>

      <Box
        flex={1}
        minW={0}
        className={`livecoach-card${attention ? ' is-accent' : ''}${nudge.addressed ? ' is-addressed' : ''}`}
        style={
          attention
            ? ({
                '--lc-accent': `var(--kula-colors-${style.palette}-dark)`,
              } as CSSProperties)
            : undefined
        }
        bg={attention ? light : 'base-white'}
        border="1px solid"
        borderColor={attention ? dark : 'secondary-100'}
        borderRadius="15px"
        p="13px 15px"
        boxShadow="0 12px 30px -22px rgba(38,34,68,0.5)"
      >
        <Flex align="center" gap="8px" mb="8px">
          <Box
            className="livecoach-tag"
            bg={attention ? dark : light}
            color={attention ? 'base-white' : dark}
            px="8px"
            py="3px"
            borderRadius="6px"
            fontSize="10px"
            fontWeight={700}
            letterSpacing="0.04em"
            textTransform="uppercase"
          >
            {style.tag}
          </Box>
          {nudge.addressed && (
            <Flex align="center" gap="4px" color="status-4">
              <Check />
              <KText
                size="xs-semi-bold"
                color="status-4"
                fontSize="11px"
                fontWeight={600}
              >
                Addressed
              </KText>
            </Flex>
          )}
          <Box flex={1} />
          <KText
            className="livecoach-ctime"
            size="xs-regular"
            color="secondary-500"
            fontSize="11px"
            fontWeight={600}
          >
            {formatClock(nudge.at_ms)}
          </KText>
        </Flex>

        <KText
          className="livecoach-cquestion"
          size="sm-semi-bold"
          color="secondary-950"
          fontSize="14px"
          fontWeight={600}
          lineHeight="1.4"
        >
          {nudge.question}
        </KText>
        {nudge.evidence && (
          <KText
            className="livecoach-cevidence"
            size="xs-regular"
            color="secondary-700"
            fontSize="12px"
            fontStyle="italic"
            lineHeight="1.45"
            mt="5px"
          >
            {nudge.evidence}
          </KText>
        )}
        {onDiscuss && (
          <Flex
            as="button"
            alignSelf="flex-start"
            align="center"
            gap="6px"
            mt="11px"
            px="10px"
            py="5px"
            borderRadius="8px"
            color="primary-600"
            bg="primary-50"
            transition="0.15s"
            _hover={{ bg: 'primary-100' }}
            onClick={() => onDiscuss(nudge)}
          >
            <Sparkle />
            <KText
              size="xs-semi-bold"
              color="primary-600"
              fontSize="12px"
              fontWeight={600}
            >
              Discuss this
            </KText>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

const EmptyStream = () => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    textAlign="center"
    h="100%"
    gap="14px"
    color="secondary-500"
    pb="80px"
  >
    <Flex
      align="center"
      justify="center"
      w="64px"
      h="64px"
      borderRadius="18px"
      bg="base-white"
      border="1px solid"
      borderColor="secondary-100"
      color="primary-500"
      boxShadow="0 14px 34px -18px rgba(38,34,68,0.4)"
    >
      <svg
        viewBox="0 0 24 24"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      >
        <path
          d="M5 12l4 4 10-10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Flex>
    <KText
      size="md-semi-bold"
      color="secondary-800"
      fontSize="15px"
      fontWeight={600}
    >
      Watching for moments worth your attention
    </KText>
    <KText
      size="sm-regular"
      color="secondary-500"
      fontSize="12.5px"
      maxW="320px"
      lineHeight="1.5"
    >
      Nudges appear here in real time as the conversation and the candidate’s
      screen evolve. There’s nothing for you to do — keep your eyes on the
      candidate.
    </KText>
  </Flex>
);

interface Props {
  nudges: StreamNudge[];
  screen: ScreenContext | null;
  // The combined connection state (panel↔server + meeting feed) for the header badge.
  connection: LiveConnection;
  // The round is over and the coach handoff has taken over — the connection badge must go, even if the socket
  // is still (re)connected, so it doesn't contradict the "Interview ended" strip below.
  ended?: boolean;
  // The session was started by the demo harness (dev/staging) — show a DEMO pill so it reads as a simulation.
  demo?: boolean;
  onDiscuss?: (nudge: StreamNudge) => void;
}

export function LiveCoachStream({
  nudges,
  screen,
  connection,
  ended,
  demo,
  onDiscuss,
}: Props) {
  const streamRef = useRef<HTMLDivElement>(null);

  // Keep the latest nudge in view as the round unfolds.
  useEffect(() => {
    streamRef.current?.scrollTo?.({
      top: streamRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [nudges.length]);

  return (
    <Flex
      direction="column"
      h="100%"
      minH={0}
      position="relative"
      className="livecoach-stage"
    >
      {/* header: title + live screen-context chip */}
      <Flex
        align="flex-start"
        gap="14px"
        px="28px"
        pt="20px"
        pb="12px"
        flex="none"
      >
        <Box>
          <KText
            size="md-bold"
            color="secondary-950"
            fontSize="17px"
            fontWeight={700}
            letterSpacing="-0.02em"
          >
            Live coaching
          </KText>
          <KText
            size="xs-regular"
            color="secondary-600"
            fontSize="12.5px"
            mt="3px"
          >
            Watching the shared screen and the conversation — surfacing only
            what’s worth your attention.
          </KText>
        </Box>
        <Box flex={1} />
        {/* On-screen chip only when a screen is actually being shared — not every meeting shares one. */}
        {screen?.screen_state && (
          <Flex
            className="livecoach-ctx"
            align="center"
            gap="9px"
            maxW="300px"
            px="12px"
            py="8px"
            borderRadius="12px"
            bg="base-white"
            border="1px solid"
            borderColor="secondary-100"
            boxShadow="0 6px 18px -12px rgba(38,34,68,0.3)"
          >
            <Flex
              flex="none"
              align="center"
              justify="center"
              w="26px"
              h="26px"
              borderRadius="7px"
              bg="pastel-4-light"
              color="pastel-4-dark"
            >
              <Eye />
            </Flex>
            <Box minW={0}>
              <KText
                size="xs-bold"
                color="secondary-500"
                fontSize="10px"
                fontWeight={700}
                letterSpacing="0.05em"
                textTransform="uppercase"
              >
                On screen
              </KText>
              <KText
                className="livecoach-ctx-val"
                size="xs-semi-bold"
                color="secondary-800"
                fontSize="12px"
                fontWeight={600}
                mt="1px"
                noOfLines={1}
              >
                Watching the shared screen
              </KText>
            </Box>
          </Flex>
        )}
        {!ended && (
          <Flex align="center" gap="7px">
            {demo && <DemoBadge />}
            <ConnectionBadge connection={connection} />
          </Flex>
        )}
      </Flex>

      {/* the stream */}
      <Box
        ref={streamRef}
        flex={1}
        minH={0}
        overflowY="auto"
        className="livecoach-scroll"
        px="28px"
        pb="96px"
      >
        {nudges.length === 0 ? (
          <EmptyStream />
        ) : (
          nudges.map((n, i) => (
            <NudgeCard
              key={n.id}
              nudge={n}
              isLast={i === nudges.length - 1}
              onDiscuss={onDiscuss}
            />
          ))
        )}
      </Box>
    </Flex>
  );
}

export default LiveCoachStream;
